import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { useNavigate } from "react-router";
import { SortOrder } from "../../../api/goodsin";
import { InboundDelivery } from "../../../shared/types";
import { FadeIn } from "../../../interface/shared/animations";

type GoodsInTableProps = {
    loading: boolean;
    inboundDeliveries: InboundDelivery[];
    sortField: string;
    sortOrder: SortOrder;
    setSort: (field: string) => void;
    page: number;
    total: number;
    perPage: number;
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
};

export default function GoodsInTable({
    loading,
    inboundDeliveries,
    sortField,
    sortOrder,
    setSort,
    page,
    total,
    perPage,
    setPage,
    setPerPage,
}: GoodsInTableProps) {
    const navigate = useNavigate();

    return (
        <Card variant="outlined">
            <CardContent>
                {loading ? (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Arrival</TableCell>
                                <TableCell>VRN</TableCell>
                                <TableCell>Reference</TableCell>
                                <TableCell>Temperature</TableCell>
                                <TableCell align="center">Pallets</TableCell>
                                <TableCell align="center">Paperwork</TableCell>
                                <TableCell align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell align="center">
                                        <Box alignItems="center" justifyContent="center" display="flex"><Skeleton variant="circular" width={20} height={20} /></Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box alignItems="center" justifyContent="center" display="flex"><Skeleton variant="circular" width={20} height={20} /></Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box alignItems="center" justifyContent="end" display="flex">
                                            <Skeleton variant="rounded" width={70} height={30} />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <FadeIn>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sortDirection={sortField === "arrived_at" ? sortOrder : false}>
                                        <TableSortLabel
                                            active={sortField === "arrived_at"}
                                            direction={sortField === "arrived_at" ? sortOrder : "asc"}
                                            onClick={() => setSort("arrived_at")}
                                        >
                                            Arrival
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sortDirection={sortField === "vrn" ? sortOrder : false}>
                                        <TableSortLabel
                                            active={sortField === "vrn"}
                                            direction={sortField === "vrn" ? sortOrder : "asc"}
                                            onClick={() => setSort("vrn")}
                                        >
                                            VRN
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sortDirection={sortField === "reference" ? sortOrder : false}>
                                        <TableSortLabel
                                            active={sortField === "reference"}
                                            direction={sortField === "reference" ? sortOrder : "asc"}
                                            onClick={() => setSort("reference")}
                                        >
                                            Reference
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sortDirection={sortField === "temp" ? sortOrder : false}>
                                        <TableSortLabel
                                            active={sortField === "temp"}
                                            direction={sortField === "temp" ? sortOrder : "asc"}
                                            onClick={() => setSort("temp")}
                                        >
                                            Temperature
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="center">Pallets</TableCell>
                                    <TableCell align="center">Paperwork</TableCell>
                                    <TableCell align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {inboundDeliveries.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7}>
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                                No deliveries found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : inboundDeliveries.map((delivery) => (
                                    <TableRow key={`${delivery.identifier}`}>
                                        <TableCell>{delivery.arrived_at.toLocaleDateString()}</TableCell>
                                        <TableCell>{delivery.vrn}</TableCell>
                                        <TableCell>{delivery.reference}</TableCell>
                                        <TableCell>{delivery.temp}°C</TableCell>
                                        <TableCell align="center">{delivery.pallets.length}</TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={`${delivery.attachments.length} attachments`}>
                                                {delivery.attachments.length > 0 ? (
                                                    <CheckCircleRoundedIcon color="success" fontSize="small" />
                                                ) : (
                                                    <CancelRoundedIcon color="error" fontSize="small" />
                                                )}
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                onClick={() => navigate(`/goods-in/deliveries/${delivery.id}`)}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page - 1}
                            onPageChange={(_, newPage) => setPage(newPage + 1)}
                            rowsPerPage={perPage}
                            onRowsPerPageChange={(e) => {
                                setPerPage(parseInt(e.target.value, 10));
                                setPage(1);
                            }}
                            rowsPerPageOptions={[10, 15, 25, 50]}
                        />
                    </FadeIn>
                )}
            </CardContent>
        </Card>
    );
}
